import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';

import { APP_VERSION } from '../config';
import { stripTrailingSlash } from '../helpers';
import { useRouteContext } from '../RouteProvider';
import { defaultLanguage, languageToFBLang } from '../services/Language';
import { useTranslationContext } from '../TranslationProvider';

import { useOptionalTranslate } from './Translate';

interface CanonicalProps {
  canonicalUrl?: string;
  canonicalPath?: string;
  canonicalParams?: Record<string, string>;
  canonicalQuery?: string | Record<string, Primitives>;
  excludeQueryFromCanonical?: boolean;
}

interface SharedProps extends CanonicalProps {
  title?: string;
  description?: string;
  titleData?: Record<string, string>;
  descriptionData?: Record<string, string>;
  hideCanonical?: boolean;
  hideAlternates?: boolean;
  hideOpenGraph?: boolean;
  hideTitle?: boolean;
  hideDescription?: boolean;
}

interface MetaTagsContext extends SharedProps {
  waitFor?: Promise<void>;
}

const context = React.createContext<MetaTagsContext>({});

export function MetaTagsProvider({ children, ...props }: React.PropsWithChildren<MetaTagsContext>) {
  const ctx = React.useContext(context);
  return <context.Provider value={{ ...ctx, ...props }}>{children}</context.Provider>;
}

type MetaTagsProps = SharedProps;

export function useCanonicalUrl(props: CanonicalProps, prefixed = false) {
  const { generateCanonicalUrl, getPrefixedPath } = useRouteContext();
  const canonicalPath = useMemo(() => {
    if (props.canonicalUrl) return props.canonicalUrl;
    const result = generateCanonicalUrl(
      props.canonicalPath,
      props.canonicalParams,
      props.excludeQueryFromCanonical ? '' : props.canonicalQuery,
    );
    if (prefixed) return getPrefixedPath(result);
    return result;
  }, [
    props.canonicalUrl,
    props.canonicalPath,
    props.canonicalParams,
    props.canonicalQuery,
    props.excludeQueryFromCanonical,
    generateCanonicalUrl,
  ]);
  return stripTrailingSlash(canonicalPath);
}

export default function MetaTags(props: React.PropsWithChildren<MetaTagsProps>) {
  const ctx = React.useContext(context);
  const title = props.title ?? ctx.title ?? 'meta.title';
  const description = props.description ?? ctx.description ?? 'meta.description';
  const tTitle = useOptionalTranslate(title, props.titleData);
  const tDescription = useOptionalTranslate(description, props.descriptionData);
  const { parametrized, pathWithoutPrefix } = useRouteContext();
  const { language } = useTranslationContext();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const hideCanonical = props.hideCanonical ?? ctx.hideCanonical;
  const hideAlternates = props.hideAlternates ?? ctx.hideAlternates;
  const hideOpenGraph = props.hideOpenGraph ?? ctx.hideOpenGraph;
  const hideTitle = props.hideTitle ?? ctx.hideTitle;
  const hideDescription = props.hideDescription ?? ctx.hideDescription;

  if (parametrized && !mounted) {
    return null;
  }

  return (
    <Helmet>
      {!hideTitle ? <title>{`HoppyGo.com - ${tTitle}`}</title> : null}
      {!hideDescription ? <meta content={tDescription} name="description" /> : null}
      <meta content={APP_VERSION} name="ui-version" />
      <html lang={language} />

      {!hideCanonical ? <CanonicalUrl {...props} /> : null}
      {!hideAlternates ? <AlternateUrls path={pathWithoutPrefix} /> : null}
      {!hideOpenGraph ? <OpenGraph description={description} title={title} {...props} /> : null}

      <meta content="app-id=1184613465" name="apple-itunes-app" />

      <script type="application/ld+json">{generateStructuredData()}</script>

      {props.children}
    </Helmet>
  );
}

export function CanonicalUrl(props: CanonicalProps) {
  const canonicalUrl = useCanonicalUrl(props, true);
  return <link href={`${process.env.GATSBY_SITE_URL}${canonicalUrl}`} rel="canonical" />;
}

export function AlternateUrls({ path }: { path: string }) {
  const { languages } = useTranslationContext();

  const createUrlWithLang = useCallback(
    (lng: string, skipLanguagePrefix: boolean) => {
      const prefix = skipLanguagePrefix ? '' : `/${lng}`;
      return stripTrailingSlash(`${process.env.GATSBY_SITE_URL}${prefix}${path}`);
    },
    [path],
  );

  return (
    <>
      {languages.map(lang => (
        <link href={createUrlWithLang(lang, false)} hrefLang={lang} key={lang} rel="alternate" />
      ))}
      <link href={createUrlWithLang(defaultLanguage, true)} hrefLang="x-default" rel="alternate" />
    </>
  );
}

export function OpenGraph({ title, description, ...props }: SharedProps) {
  const tTitle = useOptionalTranslate(title, props.titleData);
  const tDescription = useOptionalTranslate(description, props.descriptionData);
  const canonicalPath = useCanonicalUrl(props);
  const { language, languages } = useTranslationContext();

  return (
    <>
      <meta content="website" property="og:type" />
      <meta content="HoppyGo.com" property="og:site_name" />
      <meta content={process.env.GATSBY_FACEBOOK_APP_ID} property="fb:app_id" />
      <meta content={`${process.env.GATSBY_SITE_URL}${canonicalPath}`} property="og:url" />
      <meta content={`HoppyGo.com - ${tTitle}`} property="og:title" />
      <meta content={tDescription} property="og:description" />
      <meta content={`${process.env.GATSBY_SITE_URL}/img/og/ogimage-${language}.jpg`} property="og:image" />
      <meta content={languageToFBLang(language)} property="og:locale" />
      {languages.map(lang =>
        language === lang ? null : (
          <meta content={languageToFBLang(lang)} key={`og-locale-${lang}`} property="og:locale:alternate" />
        ),
      )}
    </>
  );
}

function generateStructuredData() {
  const ldJson = [];

  // iOS
  ldJson.push({
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'HoppyGo',
    operatingSystem: 'Android',
    applicationCategory: 'https://schema.org/TravelApplication',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'CZK',
    },
  });

  // Android
  ldJson.push({
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'HoppyGo',
    operatingSystem: 'iOS',
    applicationCategory: 'https://schema.org/TravelApplication',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'CZK',
    },
  });

  return JSON.stringify(ldJson);
}
